import {
  Box,
  Center,
  Flex,
  LayoutProps,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import { Document, DocumentType } from '@pelicargo/types';
import { convertFileToBase64 } from '@pelicargo/ui';
import { omit } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { DocumentChip } from '../../components/Documents/DocumentChip';
import { trpc } from '../../config/trpc';

type Props = {
  label: string;
  requestId?: number;
  document?: Partial<Document> | null;
  onDocumentChange?: (document: Partial<Document> | null) => void;
  height?: LayoutProps['h'];
  /** Floor for the upload box itself, so it can line up with cards beside it. */
  minH?: LayoutProps['minH'];
  /** Document type to upload as. Defaults to the AWB use-case. */
  documentType?: DocumentType;
  /** Trailing text in the upload prompt (e.g. "AWB or Invoice", "SLI"). */
  uploadHint?: string;
  /** Show the "or / Fill out Form below" helper beneath the box. */
  showFormHint?: boolean;
};

export const AwbUploadCard = ({
  label,
  requestId,
  document,
  onDocumentChange,
  height = 'auto',
  minH,
  documentType = DocumentType.AIRWAY_BILL,
  uploadHint = 'AWB or Invoice',
  showFormHint = true,
}: Props) => {
  const toast = useToast({ position: 'bottom', duration: 4000 });
  const uploadBg = useColorModeValue('white', 'gray.700');
  const dashedBorderColor = useColorModeValue('gray.300', 'whiteAlpha.400');
  const solidBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const [currentDocument, setCurrentDocument] = useState<Partial<Document> | null>(document ?? null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: uploadDocument } = trpc.uploadDocument.useMutation();
  const { mutateAsync: deleteDocument } = trpc.deleteDocument.useMutation();

  useEffect(() => {
    if (document?.id) setCurrentDocument(document);
    // Only hydrate from parent when a saved document id is available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.id]);

  const updateDocument = useCallback(
    (nextDocument: Partial<Document> | null) => {
      setCurrentDocument(nextDocument);
      onDocumentChange?.(nextDocument);
    },
    [onDocumentChange],
  );

  trpc.onDocumentUpdated.useSubscription(
    { requestId: requestId ?? 0 },
    {
      enabled: !!requestId && !!currentDocument?.id,
      onData: (data) => {
        if (data.id !== currentDocument?.id) return;
        updateDocument(data);
      },
    },
  );

  trpc.onDocumentDeleted.useSubscription(
    { requestId: requestId ?? 0 },
    {
      enabled: !!requestId && !!currentDocument?.id,
      onData: (deletedId) => {
        if (deletedId !== currentDocument?.id) return;
        updateDocument(null);
      },
    },
  );

  const uploadFile = useCallback(
    async (file: File) => {
      if (!requestId) {
        toast({ title: 'Error', description: 'Request is not available for upload.', status: 'error' });
        return;
      }

      try {
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > 10) throw new Error('File size exceeds 10MB. Please choose a smaller file.');

        setIsUploading(true);
        const newDocument = await uploadDocument({
          content: await convertFileToBase64(file),
          filename: file.name,
          content_type: file.type,
          document_type: documentType,
          request_id: requestId,
        });

        if (!newDocument?.id) throw new Error('Something went wrong. Please try again.');

        updateDocument(newDocument);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to upload file.',
          status: 'error',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [documentType, requestId, toast, updateDocument, uploadDocument],
  );

  const handleDelete = useCallback(async () => {
    if (!currentDocument?.id) return;

    try {
      setIsDeleting(true);
      await deleteDocument({ id: currentDocument.id });
      updateDocument(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete file.',
        status: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [currentDocument?.id, deleteDocument, toast, updateDocument]);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (files) => uploadFile(files[0]),
    noClick: !!currentDocument?.id,
    multiple: false,
    disabled: isUploading || isDeleting,
  });

  const inputProps = getInputProps();

  const renderUploadPrompt = () => (
    <Center flexDirection="column" gap="2" h={height} cursor="pointer">
      <Text fontSize="sm" color="text.subtle" textAlign="center">
        <Text as="span" color="secondary.500" fontWeight="medium">
          {isUploading ? 'Uploading...' : 'Click to upload'}
        </Text>{' '}
        {uploadHint}
      </Text>
    </Center>
  );

  const renderUploadedDocument = () => (
    <Flex align="center" minH={height}>
      <DocumentChip
        document={currentDocument}
        fallbackLabel={`${label} document`}
        removeAriaLabel={`Remove ${label} document`}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </Flex>
  );

  return (
    <Stack spacing="1.5">
      <Box
        borderWidth="1px"
        borderColor={currentDocument?.id ? solidBorderColor : dashedBorderColor}
        borderStyle={currentDocument?.id ? 'solid' : 'dashed'}
        borderRadius="lg"
        px={currentDocument?.id ? '3' : '6'}
        py={currentDocument?.id ? '2.5' : '8'}
        minH={minH}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        bg={uploadBg}
        {...getRootProps()}
        {...(currentDocument?.id ? { onClick: open } : {})}
        cursor="pointer"
        _hover={currentDocument?.id ? { borderColor: dashedBorderColor } : undefined}
      >
        <VisuallyHiddenInput type="file" display="none" {...omit(inputProps, 'size')} />
        {currentDocument?.id ? renderUploadedDocument() : renderUploadPrompt()}
      </Box>
      {showFormHint ? (
        <>
          <Text fontSize="xs" color="text.placeholder" textAlign="center" lineHeight="1">
            or
          </Text>
          <Text fontSize="xs" color="text.subtle" textAlign="center" fontWeight="medium">
            Fill out Form below
          </Text>
        </>
      ) : null}
    </Stack>
  );
};
